import React from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  AuthenticationFlowType,
  DomainPolicy,
} from '../../../../../../typings/api/skymap/rest/v1/.common';
import {
  buildInvitationUrl,
  startInvitationAuthorizationRequest,
} from '../../../../services/authentication/zitadel-invitation';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../utils/variables';
import { usePasswordConstraints } from '../../../hooks/use-password-constraints';
import { Button } from '../../button/button';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { PageProperty } from '../../profile-page/page-property';
import { isEmptyField } from '../../register-company/utils';
import { Stack } from '../../stack/stack';
import { TermsOfServiceCheckBox } from '../../terms-of-service-check-box/terms-of-service-check-box';
import { TextBox } from '../../text-box/text-box';
import { Form, Grid } from '../styles';
import { ChooseAuthentication } from './choose-authentication';
import { InvitationAccepted } from './invitation-accepted';

type Form = {
  firstName: string;
  lastName: string;
  password: string;
  phone: string;
  tosChecked: boolean;
};

enum Status {
  Idle,
  InProgress,
  Success,
  Error,
}

type Props = {
  companyName: string;
  /**
   *  Whether this invitation is completed through Zitadel, as reported by
   *  `getCompanyAdminRequest`. When it is, SkyMap never sees a password: the form collects only
   *  the person's name and phone, and they are sent to Zitadel's own page to choose one.
   */
  isZitadel: boolean;
  requestId: string;
  userEmail: string;
};

const NewCompanyAdminForm = (props: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState(Status.Idle);
  const [termsOfUseAccepted, setTermsOfUseAccepted] = React.useState(false);
  const [authenticationFlow, setAuthenticationFlow] = React.useState<
    AuthenticationFlowType | undefined
  >();
  const [domainPolicy, setDomainPolicy] = React.useState<DomainPolicy | null>(null);
  const [isLoadingDomainPolicy, setIsLoadingDomainPolicy] = React.useState(true);
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<Form>({
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      phone: '',
      tosChecked: false,
    },
  });
  const passwordConstraints = usePasswordConstraints({ password: watch('password') });

  // Check if password authentication should be hidden based on domain policy
  const shouldHidePasswordOption =
    domainPolicy?.isEnforced && domainPolicy.policyType === 'SSO_REQUIRED';

  const acceptInvitation = async (data: Form) => {
    // Prevent password authentication if SSO is enforced. It cannot apply on the Zitadel path,
    // which never collects or submits a password in the first place.
    if (!props.isZitadel && shouldHidePasswordOption) {
      setStatus(Status.Error);
      return;
    }

    try {
      setStatus(Status.InProgress);
      const companyService = SkyMapAxiosServiceFactory.instance.createUserServiceV0();
      // The backend refuses a password on the Zitadel path with a 400, so it has to be left off
      // the request rather than sent empty.
      const response = await companyService.acceptCompanyAdminRequest({
        body: {
          requestId: props.requestId,
          user: {
            firstName: data.firstName,
            lastName: data.lastName,
            password: props.isZitadel ? undefined : data.password,
            phoneNumber: data.phone === '' ? undefined : data.phone,
          },
        },
      });

      // Present only while the person still has to choose a first password. See
      // verify-form.tsx, which the Zitadel path here mirrors, for why this goes through this
      // environment's own authorization request rather than the link as it came.
      const setPasswordUrl = response.setPassword?.url;
      if (isDefined(setPasswordUrl)) {
        const requestId = await startInvitationAuthorizationRequest();
        window.location.replace(buildInvitationUrl(setPasswordUrl, requestId));
        return;
      }

      setStatus(Status.Success);
    } catch (err) {
      setStatus(Status.Error);
    }
  };

  React.useEffect(() => {
    const fetchDomainPolicy = async () => {
      try {
        setIsLoadingDomainPolicy(true);
        const authService = SkyMapAxiosServiceFactory.instance.createAuthServiceV1();
        const response = await authService.getDomainPolicy({
          query: { type: 'email', value: props.userEmail },
        });
        setDomainPolicy(response.data);
      } catch (error) {
        // If domain policy fetch fails, we'll allow both authentication methods
        setDomainPolicy(null);
      } finally {
        setIsLoadingDomainPolicy(false);
      }
    };

    void fetchDomainPolicy();
  }, [props.userEmail]);

  React.useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name === 'tosChecked') {
        if (values.tosChecked === false) {
          setAuthenticationFlow(undefined);
        }
        setTermsOfUseAccepted(Boolean(values.tosChecked));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Shared by both flows below: a brand-new person's name and phone are needed whether they
  // finish with a password here or with Zitadel.
  const renderNameAndPhoneFields = () => (
    <>
      <LabelledContainer
        required={true}
        text={t('newCompanyAdmin.newUser.form.firstName', { ns: 'components' })}
      >
        {(formElementId) => (
          <TextBox
            {...register('firstName', {
              required: true,
              validate: (value) => !isEmptyField(value),
            })}
            disabled={status === Status.InProgress}
            errorMessage={errors.firstName?.message}
            id={formElementId()}
            maxLength={255}
          />
        )}
      </LabelledContainer>

      <LabelledContainer
        required={true}
        text={t('newCompanyAdmin.newUser.form.lastName', { ns: 'components' })}
      >
        {(formElementId) => (
          <TextBox
            {...register('lastName', {
              required: true,
              validate: (value) => !isEmptyField(value),
            })}
            disabled={status === Status.InProgress}
            errorMessage={errors.lastName?.message}
            id={formElementId()}
            maxLength={255}
          />
        )}
      </LabelledContainer>

      <LabelledContainer text={t('newCompanyAdmin.newUser.form.phone', { ns: 'components' })}>
        {(formElementId) => (
          <TextBox
            {...register('phone', {
              validate: (value) => value === '' || !isEmptyField(value),
            })}
            disabled={status === Status.InProgress}
            errorMessage={errors.phone?.message}
            id={formElementId()}
            maxLength={255}
          />
        )}
      </LabelledContainer>
    </>
  );

  const renderPasswordBasedAuthenticationFlow = () => (
    <Stack direction="column" spacing={1.5}>
      <Grid>
        {renderNameAndPhoneFields()}

        <LabelledContainer
          required={true}
          text={t('newCompanyAdmin.newUser.form.password', { ns: 'components' })}
        >
          {(formElementId) => (
            <>
              <TextBox
                {...register('password', {
                  required: true,
                  validate: () => passwordConstraints.isValid,
                })}
                disabled={status === Status.InProgress}
                errorMessage={errors.password?.message}
                id={formElementId()}
                maxLength={255}
                type="password"
              />
              {passwordConstraints.render()}
            </>
          )}
        </LabelledContainer>
      </Grid>

      {status === Status.Error && (
        <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
          {t('registerCompany.step3.registrationFailure', { ns: 'components' })}
        </InfoBox>
      )}

      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
        <Button
          color="secondary"
          type="button"
          variant={'contained'}
          onClick={() => setAuthenticationFlow(undefined)}
        >
          {t('back', { ns: 'common' })}
        </Button>
        <Button color="primary" type="submit" variant={'contained'}>
          {t('newCompanyAdmin.newUser.submitFormButton', { ns: 'components' })}
        </Button>
      </Stack>
    </Stack>
  );

  // The invitation already decided how this person signs in, so there is no authentication
  // choice to offer and no password to collect. They fill in their name and are handed to
  // Zitadel to choose a password. Mirrors verify-form.tsx's Zitadel path.
  const renderZitadelAuthenticationFlow = () => (
    <Stack direction="column" spacing={1.5}>
      <Grid>{renderNameAndPhoneFields()}</Grid>

      {status === Status.Error && (
        <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
          {t('registerCompany.step3.registrationFailure', { ns: 'components' })}
        </InfoBox>
      )}

      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
        <Button
          color="primary"
          disabled={!termsOfUseAccepted}
          loading={status === Status.InProgress}
          type="submit"
          variant={'contained'}
        >
          {t('newCompanyAdmin.newUser.submitFormButton', { ns: 'components' })}
        </Button>
      </Stack>
    </Stack>
  );

  const renderAcceptView = () => {
    return (
      <Form onSubmit={handleSubmit(acceptInvitation)}>
        <InfoBox bottomMargin={true} color="yellow">
          <Trans
            components={{ bold: <strong />, br: <br /> }}
            i18nKey="newCompanyAdmin.newUser.info"
            ns="components"
            values={{ companyName: props.companyName }}
          />
        </InfoBox>

        <PageProperty
          isLoading={false}
          title={t('newCompanyAdmin.newUser.form.email', { ns: 'components' })}
          value={props.userEmail}
        />

        <TermsOfServiceCheckBox disabled={status === Status.InProgress} formControl={control} />

        {!props.isZitadel && shouldHidePasswordOption && (
          <InfoBox bottomMargin={true} color="blue">
            {t('newCompanyAdmin.newUser.ssoRequiredInfo', {
              ns: 'components',
              domain: domainPolicy?.domain,
            })}
          </InfoBox>
        )}

        {props.isZitadel ? (
          renderZitadelAuthenticationFlow()
        ) : isDefined(authenticationFlow) ? (
          renderPasswordBasedAuthenticationFlow()
        ) : (
          <ChooseAuthentication
            disabled={!termsOfUseAccepted || isLoadingDomainPolicy}
            hidePasswordOption={shouldHidePasswordOption}
            token={props.requestId}
            onPasswordSelection={() => setAuthenticationFlow('PASSWORD')}
          />
        )}
      </Form>
    );
  };

  return status === Status.Success ? (
    <InvitationAccepted companyName={props.companyName} />
  ) : (
    renderAcceptView()
  );
};

export { NewCompanyAdminForm, Props as NewCompanyAdminFormProps };
